import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { Checkbox, Heading } from '@mm/components';
import { ChatWidgetPreviewChannelOption } from '../ChatWidgetPreviewFrame/ChatWidgetPreviewFrame';
import { CustomerChatEditorAccordionItem } from '../CustomerChatEditorAccordionItem/CustomerChatEditorAccordionItem';
import { ChannelSpecificContainer } from '../../../../../../common/components/ChannelSpecificContainer/ChannelSpecificContainer';
import { ChannelSpecificDemarcator } from '../../../../../../common/components/ChannelSpecificDemarcator/ChannelSpecificDemarcator';
import { ColorPicker } from '../../../../../../common/components/ColorPicker/ColorPicker';
import './CustomerChatIconEditor.pcss';
import { ReactComponent as LeftIllustration } from './left-illustration.svg';
import { ReactComponent as RightIllustration } from './right-illustration.svg';

type Props = {
  promoter: PromoterModel;
  setPreviewChannel: (channel: ChatWidgetPreviewChannelOption) => void;
  usedInSlideout?: boolean;
  onAlertTextClick?: () => void;
};

const getInitialLocalPromoter = (promoter: PromoterModel) => {
  return {
    ...promoter,
    shape: promoter.shape || 'square',
    color: promoter.color || '#0084FF',
    shadow: promoter.shadow || false,
    position: promoter.position || 'bottom_right',
    disableAnimation: promoter.disableAnimation || false,
  };
};

export const CustomerChatIconEditor = (props: Props) => {
  const { promoter, setPreviewChannel, usedInSlideout, onAlertTextClick } =
    props;

  const chatbot = useSelector(activeChatbotSelector);
  const promoterCRUDActions = usePromoterCRUDActions();
  const chatbotCRUDActions = useChatbotCRUDActions();
  const [localPromoter, setLocalPromoter] = useState(
    getInitialLocalPromoter(promoter),
  );

  const webchatDisabled = chatbot?.chatWidgetChannel === 'facebook';

  useEffect(() => {
    setLocalPromoter(getInitialLocalPromoter(promoter));
  }, [promoter]);

  const updatePromoter = (updatedPromoter: PromoterModel) => {
    chatbotCRUDActions.updateSetupStepsRemainingByPageIdUpdated(
      updatedPromoter.pageId,
    );
    return promoterCRUDActions.update(chatbot.id, updatedPromoter);
  };

  const updatePromoterField = <K extends keyof PromoterModel>(
    key: K,
    value: PromoterModel[K],
    deferServerUpdate?: boolean,
  ) => {
    const updatedPromoter = { ...localPromoter, [key]: value };
    setLocalPromoter(updatedPromoter);

    if (!deferServerUpdate) {
      updatePromoter(updatedPromoter);
    }
  };

  const updateWebchatPromoterField = (
    ...params: Parameters<typeof updatePromoterField>
  ) => {
    if (webchatDisabled) {
      return;
    }
    updatePromoterField(...params);
    setPreviewChannel('web_chat');
  };

  return (
    <CustomerChatEditorAccordionItem
      title="Icon Settings"
      facebook={!usedInSlideout}
      webchat={!usedInSlideout}
      descriptionText="Chat icon color, shape, and position"
      usedInSlideout={usedInSlideout}
    >
      <ChannelSpecificDemarcator
        facebook
        webchat
        usedInSlideout={usedInSlideout}
      />

      <Heading size={usedInSlideout ? 5 : 4} isBold={false}>
        Icon Color
      </Heading>

      <ColorPicker
        color={localPromoter.color}
        onColorChange={color => updatePromoterField('color', color)}
        usedInSlideout={usedInSlideout}
        className="customer-chat-icon-editor__color-picker"
      />

      <ChannelSpecificContainer
        channel="web_chat"
        usedInSlideout={usedInSlideout}
        onAlertTextClick={onAlertTextClick}
      >
        <Heading size={usedInSlideout ? 5 : 4} isBold={false}>
          Shape{' '}
          {usedInSlideout && (
            <span className="customer-chat-icon-editor__channel-text">
              (Webchat only)
            </span>
          )}
        </Heading>

        <div className="customer-chat-icon-editor__shapes">
          <div
            className={classnames(
              'customer-chat-icon-editor__shape',
              'customer-chat-icon-editor__shape-circle',
              {
                'customer-chat-icon-editor__shape--disabled': webchatDisabled,
                'customer-chat-icon-editor__shape--selected':
                  localPromoter.shape === 'circle',
              },
            )}
            onClick={() => updateWebchatPromoterField('shape', 'circle')}
          />

          <div
            className={classnames(
              'customer-chat-icon-editor__shape',
              'customer-chat-icon-editor__shape-square',
              {
                'customer-chat-icon-editor__shape--disabled': webchatDisabled,
                'customer-chat-icon-editor__shape--selected':
                  localPromoter.shape === 'square',
              },
            )}
            onClick={() => updateWebchatPromoterField('shape', 'square')}
          />
          {!usedInSlideout && (
            <div className="customer-chat-icon-editor__spacer" />
          )}

          <div className="customer-chat-icon-editor__shape-and-animation-checkboxes">
            <Checkbox
              className="customer-chat-icon-editor__shape-and-animation-checkbox"
              labelHelpTextClassName={
                usedInSlideout
                  ? 'customer-chat-icon-editor__shape-and-animation-label'
                  : ''
              }
              isChecked={localPromoter.shadow}
              disabled={webchatDisabled}
              onToggle={e =>
                updateWebchatPromoterField('shadow', e.target.checked)
              }
              label="Show shape shadow"
            />
            <Checkbox
              className="customer-chat-icon-editor__shape-and-animation-checkbox"
              labelHelpTextClassName={
                usedInSlideout
                  ? 'customer-chat-icon-editor__shape-and-animation-label'
                  : ''
              }
              disabled={webchatDisabled}
              isChecked={localPromoter.disableAnimation}
              onToggle={e =>
                updateWebchatPromoterField('disableAnimation', e.target.checked)
              }
              label="Disable animation"
            />
          </div>
        </div>
        <Heading addMarginAbove size={usedInSlideout ? 5 : 4} isBold={false}>
          Position{' '}
          {usedInSlideout && (
            <span className="customer-chat-icon-editor__channel-text">
              (Webchat only)
            </span>
          )}
        </Heading>
        <div className="customer-chat-icon-editor__positions">
          <div
            className={classnames(
              'customer-chat-icon-editor__position',
              'left',
              {
                'customer-chat-icon-editor__position--disabled':
                  webchatDisabled,
                'customer-chat-icon-editor__position--selected':
                  localPromoter.position === 'bottom_left',
              },
            )}
            onClick={() =>
              updateWebchatPromoterField('position', 'bottom_left')
            }
          >
            <LeftIllustration />
            <span className="customer-chat-icon-editor__position-left-label">
              Left
            </span>
          </div>
          <div
            className={classnames(
              'customer-chat-icon-editor__position',
              'right',
              {
                'customer-chat-icon-editor__position--disabled':
                  webchatDisabled,
                'customer-chat-icon-editor__position--selected':
                  localPromoter.position === 'bottom_right',
              },
            )}
            onClick={() =>
              updateWebchatPromoterField('position', 'bottom_right')
            }
          >
            <span className="customer-chat-icon-editor__position-right-label">
              Right
            </span>
            <RightIllustration />
          </div>
        </div>
      </ChannelSpecificContainer>
    </CustomerChatEditorAccordionItem>
  );
};
